import React from 'react';
import { Formik, Form, Field, ErrorMessage, useFormikContext } from 'formik';
import { z } from 'zod';
import { toFormikValidationSchema } from 'zod-formik-adapter';
import { Button, Label, TextInput, Textarea, Select } from 'flowbite-react';
import { v4 as uuidv4 } from 'uuid';
import * as wasm from 'sched';
import { useDatabase } from '../../hooks/useDatabase';
import NodeMatcherPicker from '../common/NodeMatcherPicker';

/**
 * Form for creating a `ResourceReservation`. Two modes:
 *
 * - **Block** (`object = None`): the resource is unavailable in this window.
 * - **Reserve** (`object = Some(NodeMatcher)`): the resource is penciled in
 *   to support whatever the matcher resolves to — typically a specific task,
 *   outcome, problem, project, or tag.
 *
 * The matcher is produced by `NodeMatcherPicker` so the wire shape is the
 * externally-tagged `NodeMatcher` enum (`{ TaskId: { id } }`, `{ Tag: { tag }}`,
 * etc.) — sending the wrong shape produces the WASM
 * "invalid value: map, expected map with a single key" error.
 */

const Schema = z
  .object({
    resourceId: z.string().min(1, 'Resource is required'),
    startDate: z.string().min(1, 'Start date is required'),
    endDate: z.string().min(1, 'End date is required'),
    kind: z.enum(['block', 'reserve']),
    // Stored as JSON because zod can't easily express the externally-tagged
    // `NodeMatcher` union and we don't need form-level validation here.
    matcherJson: z.string().optional(),
    remark: z.string().optional(),
  })
  .refine((v) => v.kind !== 'reserve' || (v.matcherJson && v.matcherJson.length > 0), {
    message: 'Pick something to reserve the resource for, or switch to Block.',
    path: ['matcherJson'],
  })
  .refine((v) => v.endDate >= v.startDate, {
    message: 'End date must be on or after start date',
    path: ['endDate'],
  });

type Values = z.infer<typeof Schema>;

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (command: any) => Promise<void>;
  /** Pre-select a specific resource. */
  defaultResourceId?: string;
  /** Pre-select a specific task (for the "reserve one of these" planner flow). */
  defaultTaskId?: string;
  /** Pre-fill the start date (ISO YYYY-MM-DD). Used by drag-to-create. */
  defaultStartDate?: string;
  /** Pre-fill the end date (ISO YYYY-MM-DD). Used by drag-to-create. */
  defaultEndDate?: string;
}

const ResourceReservationForm: React.FC<Props> = ({
  isOpen,
  onClose,
  onSubmit,
  defaultResourceId,
  defaultTaskId,
  defaultStartDate,
  defaultEndDate,
}) => {
  if (!isOpen) return null;
  const { database } = useDatabase();

  const resources = database
    ? Object.entries(database.resources()).map(([id, r]: [string, any]) => ({
        id,
        name: r.name(),
      }))
    : [];

  const initialMatcher: wasm.NodeMatcher | null = defaultTaskId
    ? ({ type: 'TaskId', id: defaultTaskId } as unknown as wasm.NodeMatcher)
    : null;

  const initialValues: Values = {
    resourceId: defaultResourceId ?? '',
    startDate: defaultStartDate ?? '',
    endDate: defaultEndDate ?? defaultStartDate ?? '',
    kind: defaultTaskId ? 'reserve' : 'block',
    matcherJson: initialMatcher ? JSON.stringify(initialMatcher) : '',
    remark: '',
  };

  const handleSubmit = async (values: Values) => {
    const reservationId = uuidv4();
    const when =
      values.startDate === values.endDate
        ? ({ type: 'Day', day: values.startDate } as const)
        : ({
            type: 'Days',
            start_inclusive: values.startDate,
            end_inclusive: values.endDate,
          } as const);

    let object: wasm.NodeMatcher | undefined;
    if (values.kind === 'reserve' && values.matcherJson) {
      try {
        object = JSON.parse(values.matcherJson) as wasm.NodeMatcher;
      } catch {
        object = undefined;
      }
    }

    const command = {
      type: 'UpsertResourceReservation',
      reservation: {
        id: reservationId,
        resourceId: values.resourceId,
        when,
        object,
        remark: values.remark ? values.remark : undefined,
        reservedBy: undefined,
      },
    };
    await onSubmit(command);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-cm-bg-surface p-6 rounded-lg shadow-lg w-full max-w-md">
        <h2 className="text-xl font-semibold mb-4 text-cm-text-primary">Reserve Resource</h2>

        <Formik
          initialValues={initialValues}
          validationSchema={toFormikValidationSchema(Schema)}
          onSubmit={handleSubmit}
        >
          {({ values, isSubmitting }) => (
            <Form className="space-y-4">
              <div>
                <div className="mb-2">
                  <Label htmlFor="resourceId">Resource</Label>
                </div>
                <Field as={Select} id="resourceId" name="resourceId">
                  <option value="">— select a resource —</option>
                  {resources.map((r) => (
                    <option key={r.id} value={r.id}>{r.name}</option>
                  ))}
                </Field>
                <ErrorMessage name="resourceId" component="div" className="text-red-500 text-sm mt-1" />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="mb-2">
                    <Label htmlFor="startDate">Start date</Label>
                  </div>
                  <Field as={TextInput} type="date" id="startDate" name="startDate" />
                  <ErrorMessage name="startDate" component="div" className="text-red-500 text-sm mt-1" />
                </div>
                <div>
                  <div className="mb-2">
                    <Label htmlFor="endDate">End date</Label>
                  </div>
                  <Field as={TextInput} type="date" id="endDate" name="endDate" />
                  <ErrorMessage name="endDate" component="div" className="text-red-500 text-sm mt-1" />
                </div>
              </div>

              <KindButtonGroup />

              {values.kind === 'reserve' && (
                <div>
                  <div className="mb-2">
                    <Label htmlFor="matcherJson">Reserve for</Label>
                  </div>
                  <MatcherField database={database} />
                  <ErrorMessage name="matcherJson" component="div" className="text-red-500 text-sm mt-1" />
                </div>
              )}

              <div>
                <div className="mb-2">
                  <Label htmlFor="remark">Remark</Label>
                </div>
                <Field
                  as={Textarea}
                  id="remark"
                  name="remark"
                  rows={2}
                  placeholder={values.kind === 'block' ? 'e.g. annual calibration' : 'optional notes'}
                />
              </div>

              <div className="flex justify-end space-x-2 mt-6">
                <Button color="gray" onClick={onClose}>Cancel</Button>
                <Button type="submit" disabled={isSubmitting}>Save</Button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

// ── Internal sub-components ──────────────────────────────────────

/**
 * Two-button toggle for `kind`. Clears the matcher when switching to "block"
 * so the user doesn't carry stale state forward.
 */
const KindButtonGroup: React.FC = () => {
  const { values, setFieldValue } = useFormikContext<Values>();
  const setKind = (kind: 'block' | 'reserve') => {
    if (kind === 'block') {
      setFieldValue('matcherJson', '');
    }
    setFieldValue('kind', kind);
  };
  const baseClass =
    'flex-1 px-3 py-2 text-sm font-mono border transition-colors focus:outline-none';
  const activeClass = 'bg-cm-accent-orange text-cm-bg-deep border-cm-accent-orange';
  const inactiveClass =
    'bg-cm-bg-elevated text-cm-text-muted border-cm-border-subtle hover:text-cm-text-primary hover:border-cm-border-mid';
  return (
    <div>
      <div className="mb-2">
        <Label>Kind</Label>
      </div>
      <div className="flex">
        <button
          type="button"
          onClick={() => setKind('block')}
          className={`${baseClass} ${values.kind === 'block' ? activeClass : inactiveClass} rounded-l-lg border-r-0`}
        >
          Block (unavailable)
        </button>
        <button
          type="button"
          onClick={() => setKind('reserve')}
          className={`${baseClass} ${values.kind === 'reserve' ? activeClass : inactiveClass} rounded-r-lg`}
        >
          Reserve
        </button>
      </div>
    </div>
  );
};

/**
 * Bridges `NodeMatcherPicker` (which yields a typed `NodeMatcher`) to the
 * Formik `matcherJson` field so the form-level Zod schema can validate
 * "something was picked".
 */
const MatcherField: React.FC<{ database: wasm.WrappedDatabase | null }> = ({
  database,
}) => {
  const { values, setFieldValue } = useFormikContext<Values>();
  const matcher: wasm.NodeMatcher | null = values.matcherJson
    ? (() => {
        try {
          return JSON.parse(values.matcherJson) as wasm.NodeMatcher;
        } catch {
          return null;
        }
      })()
    : null;
  return (
    <NodeMatcherPicker
      database={database}
      value={matcher}
      onChange={(m) => setFieldValue('matcherJson', m ? JSON.stringify(m) : '')}
    />
  );
};

export default ResourceReservationForm;
